import { useCallback, useMemo } from 'react';
import { List } from 'immutable';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import I18n from 'I18n';
import { getRefreshUrl } from 'crm-events-components/internal/helpers/getRefreshUrl';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
const SINGLE_EVENT_TYPE_TRANSLATIONS = {
  ENGAGEMENTS_CALLS: 'timeline.engagements.singular.call',
  ENGAGEMENTS_CONVERSATION_SESSIONS: 'timeline.engagements.singular.conversation',
  ENGAGEMENTS_EMAILS_TRIMMED: 'timeline.engagements.singular.email',
  ENGAGEMENTS_EMAILS: 'timeline.engagements.singular.email',
  ENGAGEMENTS_LINKEDIN_MESSAGE: 'timeline.engagements.singular.linkedin-message',
  ENGAGEMENTS_MEETINGS: 'timeline.engagements.singular.meeting',
  ENGAGEMENTS_NOTES: 'timeline.engagements.singular.note',
  ENGAGEMENTS_POSTAL_MAIL: 'timeline.engagements.singular.postal-mail',
  ENGAGEMENTS_SMS: 'timeline.engagements.singular.sms',
  ENGAGEMENTS_TASKS: 'timeline.engagements.singular.task',
  ENGAGEMENTS_WHATS_APP: 'timeline.engagements.singular.whatsapp'
};
const PLURAL_EVENT_TYPE_TRANSLATIONS = {
  ENGAGEMENTS_CALLS: 'timeline.engagements.plural.call',
  ENGAGEMENTS_CONVERSATION_SESSIONS: 'timeline.engagements.plural.conversation',
  ENGAGEMENTS_EMAILS_TRIMMED: 'timeline.engagements.plural.email',
  ENGAGEMENTS_EMAILS: 'timeline.engagements.plural.email',
  ENGAGEMENTS_LINKEDIN_MESSAGE: 'timeline.engagements.plural.linkedin-message',
  ENGAGEMENTS_MEETINGS: 'timeline.engagements.plural.meeting',
  ENGAGEMENTS_NOTES: 'timeline.engagements.plural.note',
  ENGAGEMENTS_POSTAL_MAIL: 'timeline.engagements.plural.postal-mail',
  ENGAGEMENTS_SMS: 'timeline.engagements.plural.sms',
  ENGAGEMENTS_TASKS: 'timeline.engagements.plural.task',
  ENGAGEMENTS_WHATS_APP: 'timeline.engagements.plural.whatsapp'
};
function TimelineDataErrorBannerV2({
  failedOrTimedOutEvents
}) {
  const getIsNonEngagement = useCallback(eventType => !(eventType in SINGLE_EVENT_TYPE_TRANSLATIONS), []);
  const hasNonEngagementEvents = useMemo(() => failedOrTimedOutEvents.some(getIsNonEngagement), [failedOrTimedOutEvents, getIsNonEngagement]);
  const failedOrTimedOutTranslatedEvents = useMemo(() => {
    const translatedEngagements = failedOrTimedOutEvents.reduce((acc, eventType) => {
      if (getIsNonEngagement(eventType)) {
        return acc;
      }
      const translatedEngagement = hasNonEngagementEvents ? PLURAL_EVENT_TYPE_TRANSLATIONS[eventType] : SINGLE_EVENT_TYPE_TRANSLATIONS[eventType];
      return acc.push(translatedEngagement);
    }, List());
    return hasNonEngagementEvents && translatedEngagements.size ? translatedEngagements.push('timeline.engagements.other') : translatedEngagements;
  }, [failedOrTimedOutEvents, getIsNonEngagement, hasNonEngagementEvents]);
  const renderFailedOrTimedOutEvent = useCallback(eventTypeTranslation => /*#__PURE__*/_jsx(FormattedMessage, {
    message: eventTypeTranslation
  }, eventTypeTranslation), []);
  const renderedFailedOrTimedOutEventsList = useMemo(() => {
    if (failedOrTimedOutTranslatedEvents.isEmpty()) {
      return null;
    }
    return /*#__PURE__*/_jsx(FormattedList, {
      "data-unit-test": "failed-or-timed-out-events-list",
      "data-test-id": "failed-or-timed-out-events-list",
      children: failedOrTimedOutTranslatedEvents.map(renderFailedOrTimedOutEvent)
    });
  }, [failedOrTimedOutTranslatedEvents, renderFailedOrTimedOutEvent]);
  const renderedErrorSubtext = useMemo(() => {
    if (failedOrTimedOutTranslatedEvents.isEmpty()) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "timeline.timelineDataErrorBanner.errorMessageNoList_jsx",
        options: {
          href: getRefreshUrl()
        },
        elements: {
          Link
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "timeline.timelineDataErrorBanner.errorMessageWithList_jsx",
      options: {
        href: getRefreshUrl(),
        failedOrTimedOutEventsList: renderedFailedOrTimedOutEventsList
      },
      elements: {
        Link
      }
    });
  }, [failedOrTimedOutTranslatedEvents, renderedFailedOrTimedOutEventsList]);
  return /*#__PURE__*/_jsx(View, {
    paddingInline: "400",
    as: "div",
    children: /*#__PURE__*/_jsx(Alert, {
      titleText: I18n.text('GenericGrid.error.icecream'),
      type: "danger",
      marginBlockStart: "200",
      children: renderedErrorSubtext
    })
  });
}
export default TimelineDataErrorBannerV2;